import { SStorageService } from 'src/app/shared/services/sessionStorage.service';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, from, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { ErrorModalComponent } from '../components/error-modal/error-modal.component';
import { ERROR_SERVIDOR_EN_PAGINA, SILENT_BACKGROUND } from './http-context-tokens';
import { AzureAuthService } from 'src/app/apps/auth/services/azure-auth.service';
import { AuthStoreService } from 'src/app/apps/auth/services/auth-store.service';
import { AuthService } from 'src/app/apps/auth/services/auth.service';
import { RollbarService } from '../services/rollbar.service';
import { RollbarHelperService, RollbarHttpEvent } from '../utils/rollbar-helper.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    private isRefreshing = false;
    private refreshTokenSubject = new BehaviorSubject<string | null>(null);

    sstorageService = inject(SStorageService);

    constructor(
        private router: Router,
        private dialog: MatDialog,
        private authService: AuthService,
        private authAzure: AzureAuthService,
        private authStore: AuthStoreService,
        // private rollbarHelper: RollbarHelperService
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // No interceptar el propio refresh
        if (req.url.includes('/refresh-token')) {
            return next.handle(req);
        }

        return next.handle(req).pipe(
            catchError((error: HttpErrorResponse) => {
                // Requests background marcadas (polls de fondo, mi-acceso): TODO error se
                // propaga silenciosamente al caller — sin modal, sin redirect y sin Rollbar.
                // El usuario no inició estas requests; un 500 transitorio en un poll no debe
                // abrir el modal de error global (p. ej. mi-acceso se dispara en cada login).
                // Cero cambios para el resto de requests.
                if (req.context.get(SILENT_BACKGROUND)) {
                    return throwError(() => error);
                }

                // La página maneja su propio error de servidor: sin /error-page ni modal.
                // No aplica al 401/403, que siguen el flujo normal de abajo.
                if (req.context.get(ERROR_SERVIDOR_EN_PAGINA) && (error.status === 0 || error.status >= 500)) {
                    return throwError(() => error);
                }

                if (error.status === 0) {
                    // this.rollbar.error('Error de red (status 0)', { url: req.url, method: req.method });
                    // this.rollbarHelper.logHttpEvent(RollbarHttpEvent.UNKNOWN_ERROR, req, error, {
                    //     level: 'error',
                    //     customMessage: 'Error de red o CORS (HTTP status 0)',
                    //     extraData: {
                    //         hint: 'Backend no disponible, CORS, timeout o fallo de red'
                    //     }
                    // });

                    this.router.navigate(['/error-page'], { queryParams: { code: error.status } });
                    return throwError(() => error);
                }

                const tokenExpired =
                    error.headers?.get('Token-Expired') === 'true' ||
                    error.error?.code === 'TOKEN_EXPIRED' ||
                    error.error?.errorCode === 'TOKEN_EXPIRED';

                // Solo refrescamos si es 401
                if (error.status === 401 && tokenExpired) {
                    const authHeader = req.headers.get('Authorization');
                    const currentToken = authHeader?.replace('Bearer ', '') ?? null;

                    // flujo según tipo de token
                    if (this.isAzureToken(currentToken)) {
                        return this.handleAzureTokenRenew(req, next);
                    }

                    if (this.isRefreshing) {
                        // Otra request ya está refrescando → esperar el nuevo token
                        return this.refreshTokenSubject.pipe(
                            filter((token) => token !== null),
                            take(1),
                            switchMap((token) => next.handle(this.cloneRequestWithToken(req, token!)))
                        );
                    }
                    // Iniciar refresh
                    return this.handleTokenRenew(req, next);
                }

                // 401 / 403 que NO son expiración → cerrar sesión (no seguir reintentando)
                // if (error.status === 401 || error.status === 403) {
                if (error.status === 401) {
                    // this.rollbar.warn(`HTTP ${error.status} sin expiración de token`, { url: req.url, method: req.method });
                    // this.rollbarHelper.logHttpEvent(RollbarHttpEvent.TOKEN_EXPIRED, req, error, {
                    //     customMessage: 'El token del usuario expiró'
                    // });
                    this.clearSessionAndRedirect();
                    return throwError(() => error);
                }

                if (error.status === 403) {
                    // this.rollbar.warn(`HTTP ${error.status} sin permisos`, { url: req.url, method: req.method });
                    // this.rollbarHelper.logHttpEvent(RollbarHttpEvent.NO_PERMISSION, req, error);
                    this.dialog.open(ErrorModalComponent, { data: { statusCode: error.status } });
                }

                // Otros errores
                if (error.status === 500) {
                    // this.rollbar.error(`HTTP 500 en ${req.method} ${req.url}`, { message: error.message });
                    // this.rollbarHelper.logHttpEvent(RollbarHttpEvent.SERVER_ERROR, req, error);
                    this.dialog.open(ErrorModalComponent, { data: { statusCode: error.status } });
                } else if (error.status >= 400) {
                    // this.rollbar.warn(`HTTP ${error.status} en ${req.method} ${req.url}`, { message: error.message });

                    // this.rollbarHelper.logHttpEvent(RollbarHttpEvent.UNKNOWN_ERROR, req, error, {
                    //     customMessage: `HTTP ${error.status} en ${req.method} ${req.url}`,
                    //     extraData: {
                    //         originalMessage: error.message
                    //     }
                    // });
                }

                return throwError(() => error);
            })
        );
    }

    private handleTokenRenew(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        this.isRefreshing = true;
        this.refreshTokenSubject.next(null);

        return this.authService.refreshToken().pipe(
            tap(() => console.log('Refresh token OK')),
            switchMap((response) => {
                const newToken = response?.data?.token;

                if (!newToken) {
                    // Protección extra: si el backend no envía token, tratar como fallo de refresh
                    throw new Error('No se recibió un nuevo token en el refresh.');
                }

                // Guardar token para futuras requests
                this.sstorageService.setItem('token', newToken);

                // Liberar la cola
                this.isRefreshing = false;
                this.refreshTokenSubject.next(newToken);

                // 🔁 Reintentar la request original (preservando headers y withCredentials)
                return next.handle(this.cloneRequestWithToken(req, newToken));
            }),
            catchError((err) => {
                this.isRefreshing = false;

                this.clearSessionAndRedirect();

                return throwError(() => err);
            })
        );
    }

    private cloneRequestWithToken(req: HttpRequest<any>, token: string) {
        // Conserva el header 'mod' si existe
        const currentMod = req.headers.get('mod') ?? '';

        return req.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`,
                ...(currentMod ? { mod: currentMod } : {})
            },
            withCredentials: req.withCredentials
        });
    }

    private handleAzureTokenRenew(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this.isRefreshing) {
            return this.refreshTokenSubject.pipe(
                filter((token) => token !== null),
                take(1),
                switchMap((token) => next.handle(this.cloneRequestWithToken(req, token!)))
            );
        }

        this.isRefreshing = true;
        this.refreshTokenSubject.next(null);

        return from(this.authAzure.getAccessToken()).pipe(
            switchMap((newToken) => {
                if (!newToken) {
                    throw new Error('Renovación de token Azure fallida: no se obtuvo un nuevo token.');
                }

                this.sstorageService.setItem('token', newToken);

                this.isRefreshing = false;
                this.refreshTokenSubject.next(newToken);

                return next.handle(this.cloneRequestWithToken(req, newToken));
            }),
            catchError((err) => {
                this.isRefreshing = false;
                this.clearSessionAndRedirect();
                return throwError(() => err);
            })
        );
    }

    private clearSessionAndRedirect(): void {
        this.sstorageService.removeItem('token');
        this.sstorageService.removeItem('mod');
        this.sstorageService.removeItem('userInfo');
        this.sstorageService.removeItem('selectedCompany');
        this.sstorageService.removeItem('portalId');
        // 'url' es el último menú abierto. Si sobrevive al cierre de sesión, el sidebar del
        // siguiente login navega ahí (app-sidebar.component.ts:36) aunque sea de otro portal
        // o de otro usuario: se entra a una pantalla ajena o directo a /error-page 403.
        this.sstorageService.removeItem('url');

        this.authStore.clearLoginData();
        this.authAzure.deleteInfo();

        this.router.navigate(['/login']);
    }

    private isAzureToken(token: string | null): boolean {
        if (!token) return false;

        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return (
                payload.tid ||
                payload.appid ||
                (payload.iss && (payload.iss.includes('login.microsoftonline.com') || payload.iss.includes('sts.windows.net')))
            );
        } catch {
            return false;
        }
    }
}
